/**
 * SAFE IMAGE COMPRESSION UTILITY
 * 
 * Progressive enhancement approach:
 * - Try to compress images to reduce upload time/size
 * - If compression fails, fall back to original file
 * - Never breaks the upload flow
 * - Shows progress feedback to user
 */

interface CompressionOptions {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  initialQuality?: number;
  onProgress?: (progress: number) => void;
}

interface CompressionResult {
  file: File;
  wasCompressed: boolean;
  originalSize: number;
  compressedSize: number;
  compressionRatio?: number;
}

/**
 * Safely compress an image file
 * Falls back to original file if compression fails
 */
export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<CompressionResult> {
  const {
    maxSizeMB = 10, // Compress to max 10MB output
    maxWidthOrHeight = 4096, // High quality - 4K max dimension
    initialQuality = 0.85, // Start with high quality
    onProgress
  } = options;

  const originalSize = file.size;

  // Skip compression for non-image files
  if (!file.type.startsWith('image/')) {
    console.log('[Compression] Skipping non-image file:', file.type);
    return {
      file,
      wasCompressed: false,
      originalSize,
      compressedSize: originalSize
    };
  }

  // Skip compression for GIFs (to preserve animation)
  if (file.type === 'image/gif') {
    console.log('[Compression] Skipping GIF to preserve animation');
    return {
      file,
      wasCompressed: false,
      originalSize,
      compressedSize: originalSize
    };
  }

  // Skip compression for already-small files (< 2MB)
  if (file.size < 2 * 1024 * 1024) {
    console.log('[Compression] Skipping small file (<2MB):', (file.size / 1024 / 1024).toFixed(2) + 'MB');
    return {
      file,
      wasCompressed: false,
      originalSize,
      compressedSize: originalSize
    };
  }

  try {
    onProgress?.(10);
    console.log('[Compression] Starting compression:', {
      originalSize: (originalSize / 1024 / 1024).toFixed(2) + 'MB',
      maxSizeMB,
      maxDimension: maxWidthOrHeight
    });

    // Use canvas-based compression (no external dependencies)
    const compressedFile = await compressImageWithCanvas(
      file,
      maxWidthOrHeight,
      initialQuality,
      maxSizeMB,
      onProgress
    );

    const compressedSize = compressedFile.size;
    const compressionRatio = ((1 - compressedSize / originalSize) * 100).toFixed(1);

    console.log('[Compression] Success:', {
      originalSize: (originalSize / 1024 / 1024).toFixed(2) + 'MB',
      compressedSize: (compressedSize / 1024 / 1024).toFixed(2) + 'MB',
      saved: compressionRatio + '%'
    });

    onProgress?.(100);

    return {
      file: compressedFile,
      wasCompressed: true,
      originalSize,
      compressedSize,
      compressionRatio: parseFloat(compressionRatio)
    };

  } catch (error) {
    console.error('[Compression] Failed, using original file:', error);
    
    // SAFETY: Always fall back to original file
    return {
      file,
      wasCompressed: false,
      originalSize,
      compressedSize: originalSize
    };
  }
}

/**
 * Canvas-based compression (no external dependencies)
 */
async function compressImageWithCanvas(
  file: File,
  maxDimension: number,
  quality: number,
  maxSizeMB: number,
  onProgress?: (progress: number) => void
): Promise<File> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onerror = () => reject(new Error('Failed to read file'));
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onerror = () => reject(new Error('Failed to load image'));
      
      img.onload = () => {
        try {
          onProgress?.(30);

          // Calculate new dimensions
          let { width, height } = img;
          
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = Math.round((height * maxDimension) / width);
              width = maxDimension;
            } else {
              width = Math.round((width * maxDimension) / height);
              height = maxDimension;
            }
          }

          onProgress?.(50);

          // Create canvas and compress
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }

          // Use high-quality image smoothing
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          // Draw image
          ctx.drawImage(img, 0, 0, width, height);

          onProgress?.(70);

          // Try compression with quality adjustment
          compressWithQuality(canvas, quality, maxSizeMB, file.type, (blob) => {
            if (!blob) {
              reject(new Error('Compression produced no blob'));
              return;
            }

            onProgress?.(90);

            // Create new file with same name
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            });

            resolve(compressedFile);
          });

        } catch (error) {
          reject(error);
        }
      };

      img.src = e.target?.result as string;
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Compress canvas to blob with quality adjustment
 */
function compressWithQuality(
  canvas: HTMLCanvasElement,
  quality: number,
  maxSizeMB: number,
  mimeType: string,
  callback: (blob: Blob | null) => void
) {
  const maxBytes = maxSizeMB * 1024 * 1024;
  
  canvas.toBlob((blob) => {
    if (!blob) {
      callback(null);
      return;
    }

    // If size is acceptable, use it
    if (blob.size <= maxBytes) {
      callback(blob);
      return;
    }

    // If still too large and quality can be reduced, try again
    if (quality > 0.5) {
      const newQuality = quality - 0.1;
      console.log('[Compression] File still too large, reducing quality to', newQuality);
      compressWithQuality(canvas, newQuality, maxSizeMB, mimeType, callback);
    } else {
      // Accept the blob even if larger (validation will catch it later)
      callback(blob);
    }
  }, mimeType, quality);
}

/**
 * Check if file is too large for upload
 * Provides user-friendly error message
 */
export function validateFileSize(file: File, maxSizeMB: number = 50): {
  isValid: boolean;
  error?: string;
  sizeMB: number;
} {
  const sizeMB = file.size / 1024 / 1024;
  
  if (sizeMB > maxSizeMB) {
    return {
      isValid: false,
      error: `File is too large (${sizeMB.toFixed(1)}MB). Maximum size is ${maxSizeMB}MB. Please choose a smaller file or compress it first.`,
      sizeMB
    };
  }

  return {
    isValid: true,
    sizeMB
  };
}
